import {
  createTranslator,
  detectLang,
  type Lang,
  toLang,
  type Translate,
  type TranslationNamespace,
} from '@autoplan/common'
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useSyncExternalStore } from 'react'
import { useLiveVersion } from '../hooks/useLiveVersion'
import { getTranslations, isSettled, loadTranslations, subscribeToTranslations } from './store'

export type LocaleContextValue = { lang: Lang; t: Translate }

type LocaleProviderProps = {
  language?: string | null
  namespace: TranslationNamespace
  children: ReactNode
}

export function LocaleProvider({ language, namespace, children }: LocaleProviderProps) {
  const lang = useMemo(() => toLang(language), [language])
  const getSnapshot = useCallback(() => getTranslations(namespace), [namespace])
  const getSettled = useCallback(() => isSettled(namespace), [namespace])
  const translations = useSyncExternalStore(subscribeToTranslations, getSnapshot, getSnapshot)
  const settled = useSyncExternalStore(subscribeToTranslations, getSettled, getSettled)
  const live = useLiveVersion()

  useEffect(() => {
    loadTranslations(namespace, live > 0)
  }, [namespace, live])

  const t = useMemo(() => createTranslator(lang, translations), [lang, translations])
  const value = useMemo(() => ({ lang, t }), [lang, t])

  if (!translations && !settled) return null

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

export function useLocale(): LocaleContextValue {
  const value = useContext(LocaleContext)
  if (value) return value

  const lang = detectLang()
  return { lang, t: createTranslator(lang, null) }
}

export const useLang = (): Lang => useLocale().lang

const LocaleContext = createContext<LocaleContextValue | null>(null)
